"use client"

/**
 * Token storage abstraction.
 *
 * Web phase: bearer token lives in localStorage. When the app migrates to
 * Capacitor, swap `accessTokenStore` for a native secure-storage implementation
 * (iOS Keychain / Android Keystore) — nothing else in the API layer changes.
 *
 * The token is JS-readable on web; XSS exposure is mitigated by the CSP in
 * next.config.ts, not by where we store it.
 */

const ACCESS_KEY = "psc-access-token"

export interface AccessTokenStore {
  get(): string | null
  set(token: string): void
  clear(): void
}

const webAccessTokenStore: AccessTokenStore = {
  get() {
    if (typeof window === "undefined") return null
    return window.localStorage.getItem(ACCESS_KEY)
  },
  set(token) {
    if (typeof window === "undefined") return
    window.localStorage.setItem(ACCESS_KEY, token)
  },
  clear() {
    if (typeof window === "undefined") return
    window.localStorage.removeItem(ACCESS_KEY)
  },
}

export const accessTokenStore: AccessTokenStore = webAccessTokenStore

/**
 * Short-lived registration token (scoped to `register:complete`). Used only
 * between verify-otp and set-password. In-memory only — never persisted.
 */
let registrationToken: string | null = null

export const registrationTokenStore = {
  get: () => registrationToken,
  set: (token: string | null) => {
    registrationToken = token
  },
  clear: () => {
    registrationToken = null
  },
}
